// Content loader functions for YAML files
import { parse } from 'yaml'
import type {
  Profile,
  Links,
  Availability,
  Certification,
  Education,
  Skills,
  Experience,
  Project,
} from './types'
import { SHOW_APPRENTICESHIP } from './feature-flags'

// Import YAML files as raw strings using Vite's ?raw import
// English versions
import profileEnYaml from '@/content/profile-en.yaml?raw'
import availabilityEnYaml from '@/content/availability-en.yaml?raw'
import experienceEnYaml from '@/content/experience-en.yaml?raw'
import projectsEnYaml from '@/content/projects-en.yaml?raw'

// French versions
import profileFrYaml from '@/content/profile-fr.yaml?raw'
import availabilityFrYaml from '@/content/availability-fr.yaml?raw'
import experienceFrYaml from '@/content/experience-fr.yaml?raw'
import projectsFrYaml from '@/content/projects-fr.yaml?raw'

// Language-neutral files
import linksYaml from '@/content/links.yaml?raw'
import certificationsYaml from '@/content/certifications.yaml?raw'
import educationYaml from '@/content/education.yaml?raw'
import skillsYaml from '@/content/skills.yaml?raw'

// Individual loaders with language support
export function loadProfile(lang: 'en' | 'fr' = 'en'): Profile {
  const yaml = lang === 'fr' ? profileFrYaml : profileEnYaml
  const data = parse(yaml) as { profile: Profile }
  const profile = data.profile
  if (!SHOW_APPRENTICESHIP) {
    profile.summary_short = profile.summary_short_neutral ?? profile.summary_short
    profile.summary_long = profile.summary_long_neutral ?? profile.summary_long
  }
  return profile
}

export function loadLinks(): Links {
  const data = parse(linksYaml) as { links: Links }
  return data.links
}

export function loadAvailability(lang: 'en' | 'fr' = 'en'): Availability {
  const yaml = lang === 'fr' ? availabilityFrYaml : availabilityEnYaml
  const data = parse(yaml) as { availability: Availability }
  return data.availability
}

export function loadCertifications(): Certification[] {
  const data = parse(certificationsYaml) as { certifications: Certification[] }
  return data.certifications
}

export function loadEducation(): Education[] {
  const data = parse(educationYaml) as { education: Education[] }
  return data.education
}

export function loadSkills(): Skills {
  const data = parse(skillsYaml) as { skills: Skills }
  return data.skills
}

export function loadExperience(lang: 'en' | 'fr' = 'en'): Experience[] {
  const yaml = lang === 'fr' ? experienceFrYaml : experienceEnYaml
  const data = parse(yaml) as { experience: Experience[] }
  return data.experience
}

export function loadProjects(lang: 'en' | 'fr' = 'en'): Project[] {
  const yaml = lang === 'fr' ? projectsFrYaml : projectsEnYaml
  const data = parse(yaml) as { projects: Project[] }
  return data.projects
}

// Convenience function to load all content at once
export function loadAllContent(lang: 'en' | 'fr' = 'en') {
  return {
    profile: loadProfile(lang),
    links: loadLinks(),
    availability: loadAvailability(lang),
    certifications: loadCertifications(),
    education: loadEducation(),
    skills: loadSkills(),
    experience: loadExperience(lang),
    projects: loadProjects(lang),
  }
}

export type ContentData = ReturnType<typeof loadAllContent>
